import { useState } from 'react'
import classnames from 'classnames'
import { createStructuredSelector } from 'reselect'

import ErrorMessage from '@components/ErrorMessage'
import IconButton from '@components/IconButton'

import useRequestPending from 'shared/hooks/useRequestPending'
import { useConfirm } from 'shared/hooks/useConfirm'
import featureToggles from 'shared/featureToggles'

import AssignParkerModal from 'dashboard-subscriptions/components/ParkersTab/AssignParkerModal'
import ParkerListItemOccupied from 'dashboard-subscriptions/components/ParkersTab/ParkerListItemOccupied'
import ParkerListItemUnoccupied from 'dashboard-subscriptions/components/ParkersTab/ParkerListItemUnoccupied'
import SubscriptionModalFooter from 'dashboard-subscriptions/components/SubscriptionModalFooter'

import { clearPending } from 'api/actions'
import { removeParker } from 'dashboard-subscriptions/actions'
import { REMOVE_PARKER_PROGRESS } from 'dashboard-subscriptions/constants'
import { removeParkerPendingSelector } from 'dashboard-subscriptions/selectors'

import styles from '../styles.module.scss'

const ParkersTab = ({ closeModal, products, subscriptionId, removeParker, pending }) => {
    const { error, isLoading } = useRequestPending(pending, undefined, REMOVE_PARKER_PROGRESS)
    const { confirm, ConfirmModal } = useConfirm()
    const [parkerModalFor, setParkerModalFor] = useState(null)
    const isDeactivatedProduct = product => {
        if (product.endDate) {
            const currentTime = Date.now()
            const productEndTime = new Date(product.endDate).getTime()
            return Boolean(currentTime > productEndTime)
        }
        return false
    }

    const isDisabledProduct = product => (product.extended.fieldMap.isDisabled ? true : false)

    const onRemoveParker = async (subscriptionId, memberId) => {
        const isConfirmed = await confirm()
        if (isConfirmed) {
            removeParker(subscriptionId, memberId)
        }
    }

    return (
        <>
            <ConfirmModal
                title="dashboard.subscriptions.assign.parker.warning.title"
                text="dashboard.subscriptions.assign.parker.warning.text"
            />
            {parkerModalFor && (
                <AssignParkerModal
                    closeModal={() => setParkerModalFor(null)}
                    subscriptionId={subscriptionId}
                    member={parkerModalFor}
                />
            )}
            <div className={styles.productsTab}>
                {error && <ErrorMessage messages={[error]} />}
                <ol>
                    {products.map((member, i) => (
                        <>
                            <li
                                key={i}
                                className={
                                    (isDeactivatedProduct(member) || isDisabledProduct(member)) &&
                                    styles.productDetailsDisabled
                                }>
                                <div className={classnames('d-md-flex', 'justify-content-between')}>
                                    <div className={styles.productDetails}>
                                        {member.unoccupied ? (
                                            <ParkerListItemUnoccupied
                                                member={member}
                                                openAssignParkerModal={() => setParkerModalFor(member)}
                                            />
                                        ) : (
                                            <>
                                                <ParkerListItemOccupied member={member} />
                                                {isDisabledProduct(member) && (
                                                    <Link to="/contact-us">
                                                        <IconButton
                                                            icon="message"
                                                            className={styles.iconButton}
                                                            disabled={isLoading}
                                                            label={
                                                                <T>dashboard.subscriptions.products.label.support</T>
                                                            }
                                                        />
                                                    </Link>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {!member.unoccupied && (
                                        <>
                                            <div className="flex-row row no-gutters">
                                                {!featureToggles.hasSubcriptionLite && (
                                                    <IconButton
                                                        icon="trash-inactive"
                                                        disabled={isLoading}
                                                        onClick={() => onRemoveParker(subscriptionId, member.id)}
                                                    />
                                                )}
                                                {isDisabledProduct(member) || isDeactivatedProduct(member) ? (
                                                    <button className={styles.disabledButton}>
                                                        {isDisabledProduct(member) ? (
                                                            <T>
                                                                dashboard.subscriptions.assign.parker.disabled.product
                                                            </T>
                                                        ) : (
                                                            <T>
                                                                dashboard.subscriptions.assign.parker.deactivated.product
                                                            </T>
                                                        )}
                                                    </button>
                                                ) : (
                                                    <IconButton
                                                        icon="edit-grey"
                                                        className={styles.iconButton}
                                                        disabled={isLoading}
                                                        onClick={() => setParkerModalFor(member)}
                                                        label={
                                                            <T>dashboard.subscriptions.assign.parker.edit.product</T>
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </li>
                            <hr />
                        </>
                    ))}
                </ol>
            </div>
            <SubscriptionModalFooter isLoading={false} closeModal={closeModal} />
        </>
    )
}
export default connect(createStructuredSelector({ pending: removeParkerPendingSelector }), {
    removeParker,
    clearPending,
})(ParkersTab)
